import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

const ActionButton = ({ label, icon, color = "primary", onClick, size = "small" }) => (
  <Button variant="text" size={size} color={color} startIcon={icon} onClick={onClick}>
    {label}
  </Button>
);

const MenuTreePanel = ({
  loading,
  menuTree,
  onRefresh,
  onAddCategory,
  onEditFoodType,
  onDeleteFoodType,
  onAddItem,
  onEditCategory,
  onDeleteCategory,
  onEditItem,
  onDeleteItem,
  onAddVariantGroup,
  onEditVariantGroup,
  onDeleteVariantGroup,
  onAddDirectVariant,
  onAddGroupedVariant,
  onEditVariant,
  onDeleteVariant,
}) => {
  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (!menuTree?.menu?.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            No menu found for this location.
          </Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
            Refresh
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Menu Tree
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
          Refresh
        </Button>
      </Stack>

      {menuTree.menu.map((foodType) => (
        <Accordion key={foodType.id} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography fontWeight={700}>{foodType.name}</Typography>
              {foodType.is_veg ? <Chip size="small" color="success" label="Veg" /> : null}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
              <ActionButton label="Add Category" icon={<AddIcon />} onClick={() => onAddCategory(foodType)} />
              <ActionButton label="Edit" icon={<EditIcon />} onClick={() => onEditFoodType(foodType)} />
              <ActionButton label="Delete" icon={<DeleteIcon />} color="error" onClick={() => onDeleteFoodType(foodType)} />
            </Stack>

            {(foodType.categories || []).map((category) => (
              <Accordion key={category.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{category.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    <ActionButton label="Add Item" icon={<AddIcon />} onClick={() => onAddItem(foodType, category)} />
                    <ActionButton label="Edit Category" icon={<EditIcon />} onClick={() => onEditCategory(foodType, category)} />
                    <ActionButton
                      label="Delete Category"
                      icon={<DeleteIcon />}
                      color="error"
                      onClick={() => onDeleteCategory(foodType.id, category)}
                    />
                  </Stack>

                  {(category.items || []).map((item) => (
                    <Box
                      key={item.id}
                      sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2} flexWrap="wrap">
                        <Box>
                          <Typography fontWeight={600}>
                            {item.name} {item.node_type === "item_group" ? "(Group)" : ""}
                          </Typography>
                          {item.pricing ? (
                            <Typography variant="body2" color="text.secondary">
                              Pickup: {item.pricing.pickup || 0} | Delivery: {item.pricing.delivery || 0} | Dine-in: {item.pricing.dine_in || 0}
                            </Typography>
                          ) : null}
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <ActionButton label="Edit Item" icon={<EditIcon />} onClick={() => onEditItem(foodType, category, item)} />
                          <ActionButton label="Add Group" icon={<AddIcon />} onClick={() => onAddVariantGroup(foodType, category, item)} />
                          <ActionButton label="Add Direct Variant" icon={<AddIcon />} onClick={() => onAddDirectVariant(foodType, category, item)} />
                          <ActionButton label="Delete" icon={<DeleteIcon />} color="error" onClick={() => onDeleteItem({ foodTypeId: foodType.id, categoryId: category.id, item })} />
                        </Stack>
                      </Stack>

                      {(item.variant_groups || []).map((group) => (
                        <Box key={group.id} sx={{ mt: 2, pl: 2, borderLeft: "3px solid", borderColor: "divider" }}>
                          <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                            <Typography fontWeight={600}>Variant Group: {group.name}</Typography>
                            <Stack direction="row" spacing={1}>
                              <ActionButton label="Add Variant" icon={<AddIcon />} onClick={() => onAddGroupedVariant(foodType, category, item, group)} />
                              <ActionButton label="Edit" icon={<EditIcon />} onClick={() => onEditVariantGroup(foodType, category, item, group)} />
                              <ActionButton label="Delete" icon={<DeleteIcon />} color="error" onClick={() => onDeleteVariantGroup({ foodTypeId: foodType.id, categoryId: category.id, itemId: item.id, group })} />
                            </Stack>
                          </Stack>

                          {(group.variants || []).map((variant) => (
                            <Stack key={variant.id} direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                {variant.name}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                <ActionButton label="Edit" icon={<EditIcon />} onClick={() => onEditVariant(foodType, category, item, group, variant, "group")} />
                                <ActionButton label="Delete" icon={<DeleteIcon />} color="error" onClick={() => onDeleteVariant({ foodTypeId: foodType.id, categoryId: category.id, itemId: item.id, variantGroupId: group.id, variant, type: "group" })} />
                              </Stack>
                            </Stack>
                          ))}
                        </Box>
                      ))}

                      {(item.direct_variants || []).map((variant) => (
                        <Stack key={variant.id} direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="body2">Direct Variant: {variant.name}</Typography>
                          <Stack direction="row" spacing={1}>
                            <ActionButton label="Edit" icon={<EditIcon />} onClick={() => onEditVariant(foodType, category, item, null, variant, "direct")} />
                            <ActionButton label="Delete" icon={<DeleteIcon />} color="error" onClick={() => onDeleteVariant({ foodTypeId: foodType.id, categoryId: category.id, itemId: item.id, variant, type: "direct" })} />
                          </Stack>
                        </Stack>
                      ))}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default MenuTreePanel;